import * as constant from "./Constants";
import axios from "axios";
import { CustomAlertOptions } from "./Interface";
import { Language } from "./GlobalData";
import { encode } from "base-64";

// ─── Session / Refresh State ─────────────────────────────────────────────────

let refreshPromise: Promise<string | null> | null = null;
// ─── Viewport Percentage Helpers ─────────────────────────────────────────────

export const wp = (percentage: number): number =>
  (window.innerWidth * percentage) / 100;

export const hp = (percentage: number): number =>
  (window.innerHeight * percentage) / 100;

// ─── Dynamic Alert ───────────────────────────────────────────────────────────
const showSessionExpiredAlert = (message?: string): void => {
  const existing = document.getElementById("__session-expired-overlay__");
  if (existing) {
    return;
  } // prevent duplicate overlays

  // Fall back to a generic line only if the API didn't send its own message
  const displayMessage =
    message && message.trim().length > 0
      ? message
      : "Please login again to continue.";

  const overlay = document.createElement("div");
  overlay.id = "__session-expired-overlay__";
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(2px);
    font-family: var(--font-medium, sans-serif);
  `;

  overlay.innerHTML = `
    <div style="
      background: #ffffff;
      border-radius: 24px;
      padding: 32px 28px 24px;
      width: 80vw;
      max-width: 320px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.18);
      text-align: center;
    ">
      <div style="
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #fef2f2;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 4px;
      ">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>

      <p style="
        font-size: 17px;
        font-weight: 700;
        color: #111827;
        margin: 0;
      ">Session Expired!</p>

      <p style="
        font-size: 13px;
        color: #6b7280;
        margin: 0;
        line-height: 1.5;
      ">${displayMessage}</p>

      <button id="__session-expired-ok__" style="
        margin-top: 8px;
        width: 100%;
        padding: 12px;
        background: #dc2626;
        color: #ffffff;
        border: none;
        border-radius: 12px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        font-family: var(--font-medium, sans-serif);
      ">OK</button>
    </div>
  `;

  document.body.appendChild(overlay);

  document
    .getElementById("__session-expired-ok__")
    ?.addEventListener("click", () => {
      overlay.remove();
      // Clear auth data before redirecting
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      window.location.replace("/loginmpin");
    });
};
export const showDynamicAlert = ({
  title,
  message,
  showYes = false,
  showNo = false,
  showOk = false,
  showCancel = false,
  onYes,
  onNo,
  onOk,
  onCancel,
}: CustomAlertOptions): void => {
  const fullMessage = `${title}\n\n${message}`;

  if (showYes || showNo) {
    const confirmed = window.confirm(fullMessage);
    if (confirmed && onYes) {
      onYes();
    }
    if (!confirmed && onNo) {
      onNo();
    }
    return;
  }

  if (showOk || showCancel) {
    if (showOk && showCancel) {
      const confirmed = window.confirm(fullMessage);
      if (confirmed && onOk) {
        onOk();
      }
      if (!confirmed && onCancel) {
        onCancel();
      }
    } else if (showOk) {
      window.alert(fullMessage);
      if (onOk) {
        onOk();
      }
    } else if (showCancel) {
      const confirmed = window.confirm(fullMessage);
      if (!confirmed && onCancel) {
        onCancel();
      }
    }
  }
};
// ─── Network Check ───────────────────────────────────────────────────────────

const checkNetworkConnectivity = (): boolean => {
  if (!navigator.onLine) {
    showDynamicAlert({
      title: "Sorry!",
      message: "You are offline!",
      showOk: true,
      okLabel: Language.Lbl_OK,
      cancelable: true,
    });
    return false;
  }
  return true;
};

export async function httpGetFetch(
  apiUrl: string,
  authToken?: string,
  dbType?: string,
): Promise<any> {
  const fullUrl = constant.rootUrl + apiUrl;
  if (!checkNetworkConnectivity()) {
    return null;
  }

  const headers: any = { 'Content-Type': 'application/json' };
  const hasAuth = !!authToken;
  const hasDbType = !!dbType;
  if (hasAuth) {
    headers['Authorization'] = `Bearer ${authToken?.trim()}`;
  }
  if (hasDbType) {
    headers['DBType'] = dbType!.trim();
  }
  // Always send the selected party so the API can validate it against the token
  const _userData = await getData('userData');
  const _partyId = _userData?.PARTY_ID ?? '';
  if (_partyId) {
    headers['X-Party-ID'] = _partyId;
  }

  const axiosInstance = axios.create({ timeout: 90000, headers });

  const makeRequest = async (tokenToUse: string, retry = true): Promise<any> => {
    try {
      const requestHeaders: any = { ...headers };
      if (hasAuth) {
        requestHeaders['Authorization'] = `Bearer ${tokenToUse}`;
      }
      if (hasDbType) {
        requestHeaders['DBType'] = dbType!.trim();
      }
      if (_partyId) {
        requestHeaders['X-Party-ID'] = _partyId;
      }
      const response = await axiosInstance.get(fullUrl, { headers: requestHeaders });
      // console.log('httpGetFetch response:', response);
      //console.log("================================");
       console.log("Build Environment:", process.env.REACT_APP_ENV);
      // console.log("Base URL:", process.env.REACT_APP_BASE_URL);
         if (response?.data?.Status === 2) {
        showSessionExpiredAlert(response?.data?.message);
        return null;
      }
      return response?.data;
    } catch (error: any) {
       // ── NEW: global 503 handler ──────────────────────────────────
       // Any API returning 503 (Service Unavailable) forces a logout +
       // redirect to login. This is the only place that can see it,
       // since it's caught right here and never re-thrown otherwise.
       if (error?.response?.status === 503) {
        const { forceLogout } = await import('./AuthService');
        forceLogout();
        return null;
      }
       // ── END NEW ───────────────────────────────────────────────────
       if (error?.response?.data?.Status === 2) {
        showSessionExpiredAlert(error?.response?.data?.Message);
        return null;
      }
      if (hasAuth && axios.isAxiosError(error) && error.response?.status === 401 && retry) {
        const newToken = await refreshAuthToken(authToken, dbType);
        if (newToken) {
          return await makeRequest(newToken, false);
        }
        if (document.getElementById("__session-expired-overlay__")) {
          return new Promise(() => {});
        }
      }
    }
  };

  return await makeRequest(authToken ?? "");
}

export async function httpPostFetch(
  apiUrl: string,
  body: any,
  authToken?: string,
  dbType?: string,
): Promise<any> {
  const fullUrl = constant.rootUrl + apiUrl;
  if (!checkNetworkConnectivity()) {
    return null;
  }

  const headers: any = { 'Content-Type': 'application/json' };
  const hasAuth = !!authToken;
  const hasDbType = !!dbType;
  if (hasAuth) {
    headers['Authorization'] = `Bearer ${authToken?.trim()}`;
  }
  if (hasDbType) {
    headers['DBType'] = dbType!.trim();
  }
  // Always send the selected party so the API can validate it against the token
  const _userData = await getData('userData');
  const _partyId = _userData?.PARTY_ID ?? '';
  if (_partyId) {
    headers['X-Party-ID'] = _partyId;
  }

  const axiosInstance = axios.create({ timeout: 90000, headers });

  const makeRequest = async (tokenToUse: string, retry = true): Promise<any> => {
    try {
      const requestHeaders: any = { ...headers };
      if (hasAuth) {
        requestHeaders['Authorization'] = `Bearer ${tokenToUse}`;
      }
      if (hasDbType) {
        requestHeaders['DBType'] = dbType!.trim();
      }
      if (_partyId) {
        requestHeaders['X-Party-ID'] = _partyId;
      }
      const response = await axiosInstance.post(fullUrl, body, { headers: requestHeaders });
      return response?.data;
    } catch (error: any) {
       // ── NEW: global 503 handler ──────────────────────────────────
       if (error?.response?.status === 503) {
        const { forceLogout } = await import('./AuthService');
        forceLogout();
        return null;
      }
       // ── END NEW ───────────────────────────────────────────────────
       if (error?.response?.data?.Status === 2) {
        showSessionExpiredAlert(error?.response?.data?.message);
        return null;
      }
      if (hasAuth && axios.isAxiosError(error) && error.response?.status === 401 && retry) {
        const newToken = await refreshAuthToken(authToken, dbType);
        if (newToken) {
          return await makeRequest(newToken, false);
        }
        if (document.getElementById("__session-expired-overlay__")) {
          return new Promise(() => {});
        }
      }
    }
  };

  return await makeRequest(authToken ?? "");
}

// ─── Token Refresh ────────────────────────────────────────────────────────────
export async function refreshAuthToken(
  authToken: any,
  dbType: any,
): Promise<string | null> {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return await refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const userData = await getData("userData");
      const tokenData = await getData("token");
      const refreshTokenData = await getData("refreshToken");

      if (!userData?.mobileNumber || !tokenData || !refreshTokenData) {
        throw new Error("Missing required data for token refresh");
      }

      const payload = {
        mobileNo: encode(userData.mobileNumber),
        token: encode(tokenData),
        refreshToken: encode(refreshTokenData),
      };

      const headers: any = { "Content-Type": "application/json" };
      if (dbType) {
        headers["DBType"] = dbType.trim();
      }

      const axiosInstance = axios.create({ timeout: 90000, headers });

      const fullUrl = `${constant.rootUrl}${constant.webservices.getRefreshToken}`;

      const response = await axiosInstance.post(fullUrl, payload);

      // ── Session expired — status 1 from refresh endpoint ──
      if (response?.data?.status === 1) {
        showSessionExpiredAlert(response?.data?.message);
        return null;
      }

      if (!response?.data?.data?.[0]) {
        throw new Error("Invalid refresh token response");
      }
      const newToken = response.data.data[0].Token;
      const newRefreshToken = response.data.data[0].RefreshToken;

      if (!newToken || !newRefreshToken) {
        throw new Error("Missing tokens in refresh response");
      }

      await storeData("token", newToken);
      await storeData("refreshToken", newRefreshToken);

      return newToken;
    } catch (err) {
      // Return null on error, don't throw to allow callers to handle gracefully
      return null;
    } finally {
      // Reset state after completion (success or failure)
      refreshPromise = null;
    }
  })();

  // Return the promise (not awaiting here to avoid blocking)
  return await refreshPromise;
}
// ─── Downtime Fetch ───────────────────────────────────────────────────────────

export function httpGetDowntTimeFetch(apiUrl: string): Promise<any> {
  return new Promise((resolve, reject) => {
    try {
      if (navigator.onLine) {
        const axiosInstance = axios.create({
          timeout: 90000,
          headers: { "Content-Type": "application/json" },
          validateStatus: () => true,
        });
        axiosInstance
          .get(apiUrl)
          .then((res) => resolve(res.data))
          .catch(() => reject("Something went wrong"));
      } else {
        showDynamicAlert({
          title: "Sorry!",
          message: "You are offline!",
          showOk: true,
          okLabel: Language.Lbl_OK,
          cancelable: true,
        });
        reject("no network");
      }
    } catch (error) {
      reject(error);
    }
  });
}

// ─── Validators ───────────────────────────────────────────────────────────────

export function checkForUndefined(inputStr: any): string {
  if (
    inputStr === undefined ||
    inputStr === "undefined" ||
    inputStr === null ||
    inputStr === "null" ||
    inputStr === "NULL" ||
    inputStr === "NaN" ||
    inputStr === ""
  ) {
    return "";
  }
  return inputStr;
}

// ─── Storage Helpers ──────────────────────────────────────────────────────────

export const storeData = async (key: string, item: any): Promise<void> => {
  try {
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error: any) {
    if (error.name === "QuotaExceededError") {
      // Clear old data or show warning to user
      showDynamicAlert({
        title: "Sorry!",
        message: "QuotaExceededError for Token Storage!",
        showOk: true,
        okLabel: Language.Lbl_OK,
        cancelable: true,
      });
    }
    throw error;
  }
};

export const updateData = async (key: string, newData: any): Promise<void> => {
  const existingData = localStorage.getItem(key);
  const existing = existingData ? JSON.parse(existingData) : {};
  const updated = { ...existing, ...newData };
  localStorage.setItem(key, JSON.stringify(updated));
};

export const getData = async (key: string): Promise<any> => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

export const clearData = async (key: string): Promise<void> => {
  localStorage.removeItem(key);
};

// ─── String Utilities ─────────────────────────────────────────────────────────

export const capitalizeWords = (str = ""): string =>
  str.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());

export const trimAddress = (address: string, maxLength = 18): string => {
  if (!address) {
    return "";
  }
  const parts = address.split(",");
  const trimmed = parts.length <= 2 ? address : `${parts[0]}, ${parts[1]}`;
  return trimmed.length > maxLength
    ? `${trimmed.slice(0, maxLength).trim()}...`
    : trimmed;
};
